import React from 'react';
import { Mail, MapPin, Clock, ArrowUp, ExternalLink } from 'lucide-react';
import { companyInfo, seoKeywords } from '../data/mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-950 border-t border-neutral-800">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            {/* Clean styled logo */}
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/adela-logo-symbol.png" 
                alt="" 
                className="h-11 w-11 object-contain"
                aria-hidden="true"
              />
              <div className="text-xl font-bold tracking-wide">
                <span className="text-white">ADELA</span>
                <span className="text-amber-400 ml-1.5">MERCHANTS</span>
              </div>
            </div>
            <p className="text-amber-400/80 text-sm mb-4 italic font-medium">
              "{companyInfo.tagline}"
            </p>
            <p className="text-neutral-400 text-sm leading-relaxed">
              Perth's trusted paper core manufacturer, supplying quality cardboard cores and paper tubes across Western Australia.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'About Us', 'Products', 'Services', 'Contact'].map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase().replace(' ', '')}`}
                    className="text-neutral-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-6">Our Products</h4>
            <ul className="space-y-3">
              {[
                'Paper Cores',
                'Cardboard Tubes',
                'Printing Cores',
                'Concrete Form Tubes',
                'Print Supplies',
                'Custom Orders'
              ].map((product) => (
                <li key={product}>
                  <a
                    href="#products"
                    className="text-neutral-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    {product}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-6">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-neutral-400 hover:text-amber-400 transition-colors text-sm"
                >
                  {companyInfo.email}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-400 text-sm">
                  {companyInfo.address}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-neutral-400">{companyInfo.businessHours}</p>
                  <p className="text-amber-400 text-xs mt-1">{companyInfo.walkIns}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Keywords Section (Hidden visually but good for SEO) */}
        <div className="mt-12 pt-8 border-t border-neutral-800">
          <h5 className="text-neutral-600 text-xs uppercase tracking-wider mb-4">Areas We Serve</h5>
          <div className="flex flex-wrap gap-2">
            {['Perth', 'Western Australia', 'Cannington', 'WA', 'Australia'].map((area) => (
              <span key={area} className="px-3 py-1 bg-neutral-900 text-neutral-500 text-xs rounded-full">
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-neutral-500 text-sm text-center md:text-left">
              © {currentYear} Adela Merchants. All rights reserved.
              <span className="mx-2">|</span>
              <a href="https://www.adelamerchants.com.au" className="hover:text-amber-400 transition-colors inline-flex items-center gap-1">
                {companyInfo.website}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <button
              onClick={scrollToTop}
              className="p-3 bg-neutral-900 hover:bg-amber-400 text-neutral-400 hover:text-neutral-900 rounded-lg transition-all duration-300 group"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Schema.org Structured Data for SEO (rendered as JSON-LD in head would be better) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Adela Merchants",
          "description": "Paper core manufacturer in Perth, Western Australia. Supplier of cardboard cores, paper tubes, and print supplies.",
          "url": "https://www.adelamerchants.com.au",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unit 7, 49/51 Kent Street",
            "addressLocality": "Cannington",
            "addressRegion": "Western Australia",
            "postalCode": "6107",
            "addressCountry": "AU"
          },
          "email": "Orders@AdelaMerchants.com.au",
          "openingHours": "Mo-Fr 08:00-16:00",
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -32.0167,
            "longitude": 115.9333
          },
          "areaServed": {
            "@type": "State",
            "name": "Western Australia"
          },
          "priceRange": "$$"
        })
      }} />
    </footer>
  );
};

export default Footer;
